import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBackRoute } from '../hooks/useBackRoute';
import { navigateWithFrom } from '../utils/navigationUtils';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeString, sanitizeSearchInput } from '../utils/security';
import { fetchAppointmentsByTechService, cancelAppointment } from '../services/serviceRecordsService';
import { fetchUserProfile } from '../services/authService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import DateRangeFilterModal from '../components/DateRangeFilterModal';
import TableListFooter from '../components/TableListFooter';
import { ROWS_PER_PAGE_OPTIONS } from '../constants/pagination';
import { getOnemRowClass } from '../utils/onemDerecesiHelper';

const rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS;

const safeText = (value) => sanitizeString(value ?? '');

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  if (error?.status === 404) return 'API endpoint bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';

  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

// techServiceId'yi user objesinden çıkar (string, object veya nested yapıları destekler)
const extractTechServiceId = (u) => {
  if (!u) return null;
  const ts = u.techService ?? u.techServiceId;
  if (!ts) return null;
  if (typeof ts === 'string' && ts.trim()) return ts.trim();
  if (ts && typeof ts === 'object') {
    const id = ts._id ?? ts.id;
    if (id) return typeof id === 'string' ? id : String(id);
  }
  return null;
};

const TechServiceAppointmentsPage = () => {
  const { confirm, promptText } = useConfirmDialog();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useBackRoute('/');
  const { user, isLoading: authLoading, setUser } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [filters, setFilters] = useState({ search: '', durum: '' });
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileRefetched, setProfileRefetched] = useState(false);
  const [sortBy, setSortBy] = useState(null); // 'musteri' | 'randevuTarihi' | null
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
  const [dateRange, setDateRange] = useState({});
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  const techServiceId = useMemo(() => extractTechServiceId(user), [user]);

  const handleSort = useCallback((column) => {
    setSortBy((prev) => {
      if (prev === column) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return column;
      }
      setSortOrder('asc');
      return column;
    });
  }, []);

  const sortedAppointments = useMemo(() => {
    if (!sortBy || appointments.length === 0) return appointments;
    return [...appointments].sort((a, b) => {
      let valA, valB;
      if (sortBy === 'musteri') {
        const custA = a.servisKaydi || a.servisKaydiId || {};
        const custB = b.servisKaydi || b.servisKaydiId || {};
        const nameA = `${custA.musteri?.adi || custA.musteri?.name || ''} ${custA.musteri?.soyadi || custA.musteri?.surname || ''}`.trim().toLowerCase();
        const nameB = `${custB.musteri?.adi || custB.musteri?.name || ''} ${custB.musteri?.soyadi || custB.musteri?.surname || ''}`.trim().toLowerCase();
        valA = nameA || 'zzz';
        valB = nameB || 'zzz';
        return sortOrder === 'asc' ? (valA < valB ? -1 : valA > valB ? 1 : 0) : (valA > valB ? -1 : valA < valB ? 1 : 0);
      }
      if (sortBy === 'randevuTarihi') {
        valA = a.randevuTarihi ? new Date(a.randevuTarihi).getTime() : 0;
        valB = b.randevuTarihi ? new Date(b.randevuTarihi).getTime() : 0;
        return sortOrder === 'asc' ? valA - valB : valB - valA;
      }
      return 0;
    });
  }, [appointments, sortBy, sortOrder]);

  const sanitizedSearchValue = useMemo(() => {
    const sanitized = sanitizeSearchInput(filters.search);
    return sanitized && sanitized.trim().length > 0 ? sanitized : undefined;
  }, [filters.search]);

  const filterParams = useMemo(
    () => ({
      search: sanitizedSearchValue,
      durum: filters.durum || undefined,
      baslangicTarihi: dateRange.baslangicTarihi,
      bitisTarihi: dateRange.bitisTarihi,
    }),
    [sanitizedSearchValue, filters.durum, dateRange.baslangicTarihi, dateRange.bitisTarihi]
  );

  const loadAppointments = useCallback(async () => {
    // Auth yüklenene kadar bekle
    if (authLoading) {
      setLoading(true);
      return;
    }

    // Auth yüklendi, şimdi user ve techServiceId kontrolü yap
    if (!user) {
      setError('Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
      setLoading(false);
      return;
    }

    if (!techServiceId) {
      setError('Teknik servis bilgisi bulunamadı. Lütfen yönetici ile iletişime geçin.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const requestParams = {
        page: currentPage,
        limit: rowsPerPage,
        aktif: true,
        durum: filterParams.durum,
        baslangicTarihi: filterParams.baslangicTarihi,
        bitisTarihi: filterParams.bitisTarihi,
        arama: filterParams.search,
      };
      
      const response = await fetchAppointmentsByTechService(techServiceId, requestParams);
      const list = response?.veri?.randevular || response?.veri || [];
      
      const pagination = response?.veri?.sayfalama || response?.sayfalama || {};
      const veri = response?.veri || response?.data || {};
      
      setAppointments(Array.isArray(list) ? list : []);
      
      // Pagination meta bilgilerini backend'den al, yoksa hesapla
      const total = pagination.toplam || veri?.toplam || list.length || 0;
      const totalPages = pagination.toplamSayfa || veri?.toplamSayfa || Math.ceil(total / rowsPerPage) || 1;
      const mevcutSayfa = pagination.mevcutSayfa || veri?.sayfa || currentPage;
      
      setPaginationMeta({
        total: total,
        totalPages: totalPages,
      });
      
      // Backend'den gelen sayfa numarasını kullan, ama geçerli aralıkta olduğundan emin ol
      if (mevcutSayfa && mevcutSayfa >= 1 && mevcutSayfa <= totalPages) {
        setCurrentPage(mevcutSayfa);
      } else if (mevcutSayfa > totalPages && totalPages > 0) {
        // Eğer sayfa numarası toplam sayfadan büyükse, son sayfaya git
        setCurrentPage(totalPages);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Randevular yüklenemedi'));
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  }, [techServiceId, currentPage, rowsPerPage, filterParams, user, authLoading]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // techServiceId yoksa ama kullanıcı tech-service rolündeyse profili yeniden çek
  useEffect(() => {
    if (authLoading || !user || techServiceId || profileRefetched) return;
    const rol = user.rol || user.role || '';
    if (rol !== 'tech-service') return;
    let cancelled = false;
    const refetch = async () => {
      try {
        const res = await fetchUserProfile();
        const updated = res?.veri?.kullanici ?? res?.veri ?? res?.kullanici ?? res;
        if (cancelled || !updated || !extractTechServiceId(updated)) return;
        setUser(updated);
      } catch {
        /* sessiz */
      } finally {
        if (!cancelled) setProfileRefetched(true);
      }
    };
    refetch();
    return () => { cancelled = true; };
  }, [authLoading, user, techServiceId, profileRefetched, setUser]);

  const handleSearchChange = (event) => {
    const value = sanitizeSearchInput(event.target.value, { trimEdges: false });
    setSearchInput(value);
  };

  // Debounce için search input
  useEffect(() => {
    // İlk yüklemede veya boş değerde spinner gösterme
    if (searchInput.trim().length === 0) {
      setSearchLoading(false);
      setFilters((prev) => ({ ...prev, search: '' }));
      return;
    }

    const timeoutId = setTimeout(() => {
      setSearchLoading(true);
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (rowsPerPageOptions.includes(value)) {
      setRowsPerPage(value);
      setCurrentPage(1);
    }
  };

  const handlePageChange = (direction) => {
    setCurrentPage((prev) => {
      const next = prev + direction;
      if (next < 1 || next > paginationMeta.totalPages) return prev;
      return next;
    });
  };

  const getStatusBadge = (durum) => {
    const statusColors = {
      'Randevu Bekleniyor': '#f59e0b',
      'Randevu Tarihi Bekleniyor': '#6366f1',
      'İşlemde': '#10b981',
      'Randevu Tamamlandı': '#059669',
      'Randevu İptal Edildi': '#ef4444',
    };
    
    const color = statusColors[durum] || '#64748b';
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        background: `${color}15`,
        color: color,
        border: `1px solid ${color}40`,
        whiteSpace: 'nowrap',
        display: 'inline-block'
      }}>
        {durum || '—'}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'Acil': '#ef4444',
      'Yüksek': '#f59e0b',
      'Orta': '#3b82f6',
      'Düşük': '#64748b',
    };
    
    const color = priorityColors[priority] || '#64748b';
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        background: `${color}15`,
        color: color,
        border: `1px solid ${color}40`
      }}>
        {priority || '—'}
      </span>
    );
  };

  const renderAppointmentsTable = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Randevular yükleniyor</div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      );
    }
    if (error) return <div className="error-state">{safeText(error)}</div>;
    if (appointments.length === 0) {
      return (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <i className="fas fa-calendar-times" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
          <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Randevu bulunamadı</div>
        </div>
      );
    }

    return (
      <table className="admins-table">
        <thead>
          <tr>
            <th>
              <button
                type="button"
                className="sortable-header"
                onClick={() => handleSort('musteri')}
                title="Müşteri adına göre sırala"
              >
                Müşteri
                {sortBy === 'musteri' && (
                  <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`} style={{ marginLeft: '6px', fontSize: '10px', opacity: 0.8 }}></i>
                )}
              </button>
            </th>
            <th>Telefon</th>
            <th>
              <button
                type="button"
                className="sortable-header"
                onClick={() => handleSort('randevuTarihi')}
                title="Randevu tarihine göre sırala"
              >
                Randevu Tarihi
                {sortBy === 'randevuTarihi' && (
                  <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`} style={{ marginLeft: '6px', fontSize: '10px', opacity: 0.8 }}></i>
                )}
              </button>
            </th>
            <th>Randevu Saati</th>
            <th>Önem Derecesi</th>
            <th>İşlem Tipi</th>
            <th>Durum</th>
            <th className="islemler-sutunu">İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {sortedAppointments.map((appointment) => {
            const appointmentId = appointment.id || appointment._id;
            const serviceRecord = appointment.servisKaydi || appointment.servisKaydiId || {};
            const customer = serviceRecord.musteri || serviceRecord.musteriId || {};
            const customerName = `${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim() || '—';
            const customerPhone = customer.telefon || customer.phone || customer.gsm || '—';
            const appointmentDate = appointment.randevuTarihi 
              ? new Date(appointment.randevuTarihi).toLocaleDateString('tr-TR', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })
              : '—';
            
            const onemRowClass = getOnemRowClass(serviceRecord.onemDerecesi);
            
            return (
              <tr
                key={appointmentId}
                onClick={() => appointmentId && openAppointmentDetail(appointmentId)}
                style={{ cursor: appointmentId ? 'pointer' : 'default' }}
                className={`tp-appointment-row${onemRowClass ? ` ${onemRowClass}` : ''}`}
              >
                <td>
                  {customerName}
                </td>
                <td>{customerPhone}</td>
                <td>{appointmentDate}</td>
                <td>{appointment.randevuSaati || appointment.randevuSaatAraligi || '—'}</td>
                <td>{getPriorityBadge(serviceRecord.onemDerecesi)}</td>
                <td>{serviceRecord.islemTipi || '—'}</td>
                <td className="status-cell">{getStatusBadge(appointment.durum)}</td>
                <td className="islemler-sutunu" onClick={(e) => e.stopPropagation()}>
                  {appointmentId && (
                    <div className="table-actions-inner">
                      <button
                        type="button"
                        className="category-action-btn edit-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openAppointmentDetail(appointmentId);
                        }}
                        title="Randevu detayı"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      {(user?.rol || '').toLowerCase() === 'tech-service' &&
                        appointment.durum === 'Randevu Tarihi Bekleniyor' &&
                        !appointment.iptalBilgileri &&
                        !appointment.iptalNedeni && (
                        <button
                          type="button"
                          className="category-action-btn delete-btn"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            const servisKaydiId = appointment.servisKaydiId || appointment.servisKaydi?.id || appointment.servisKaydi?._id;
                            if (!servisKaydiId) {
                              if (window.showNotification) {
                                window.showNotification('error', 'Servis kaydı bilgisi bulunamadı.');
                              }
                              return;
                            }
                            
                            const confirmMessage =
                              'Bu randevuyu iptal etmek istediğinize emin misiniz?\n\n' +
                              '⚠️ UYARI: Bu işlem geri alınamaz!\n\n' +
                              'Randevu iptal edildiğinde:\n' +
                              '• Tüm atamalar (teknisyen, işlemler, parçalar) silinecek\n' +
                              '• Garanti ve fatura bilgileri temizlenecek\n' +
                              '• Servis kaydı "Randevu Bekleniyor" durumuna dönecek';

                            const confirmedCancel = await confirm({
                              title: 'Randevu iptali',
                              message: confirmMessage,
                              variant: 'danger',
                              confirmLabel: 'Evet, iptal et',
                            });
                            if (!confirmedCancel) return;

                            const iptalNedeni = await promptText({
                              title: 'İptal nedeni',
                              message: 'İptal nedeni en az 10 karakter olmalıdır.',
                              placeholder: 'İptal nedeni',
                              minLength: 10,
                              multiline: true,
                            });

                            if (iptalNedeni === null) return;

                            try {
                                // Randevuyu iptal et (backend zaten servis kaydı durumunu güncelliyor)
                                await cancelAppointment(servisKaydiId, appointmentId, iptalNedeni, user?.rol);
                                
                                if (window.showNotification) {
                                  window.showNotification('success', 'Randevu iptal edildi. Servis kaydı durumu "Randevu Bekleniyor" olarak güncellendi.');
                                }
                                
                                // Randevuları yeniden yükle
                                await loadAppointments();
                              } catch (error) {
                                console.error('Randevu iptal hatası:', error);
                                if (window.showNotification) {
                                  const errorMessage = error?.message || error?.data?.mesaj || 'Randevu iptal edilemedi';
                                  window.showNotification('error', errorMessage);
                                }
                              }
                          }}
                          title="Randevu İptal Et"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const totalRecords = paginationMeta.total || appointments.length;

  const dateFilterLabel = dateRange.baslangicTarihi || dateRange.bitisTarihi
    ? `${dateRange.baslangicTarihi || '…'} – ${dateRange.bitisTarihi || '…'}`
    : null;

  const openAppointmentDetail = (appointmentId) => {
    if (!appointmentId) return;
    navigateWithFrom(navigate, `/appointments/${appointmentId}`, location);
  };

  // Auth yükleniyorsa loading göster
  if (authLoading) {
    return (
      <main className="main-content tech-service-appointments">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Yükleniyor...</div>
        </div>
      </main>
    );
  }

  // Teknik servis ID yoksa: profili yeniden çekmeyi denediysek hata, yoksa loading
  if (!techServiceId && user) {
    const isTechServiceOrPersonRole = ['tech-service', 'tech-person'].includes((user.rol || user.role || '').toLowerCase());
    if (isTechServiceOrPersonRole && !profileRefetched) {
      return (
        <main className="main-content tech-service-appointments">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Profil bilgisi yükleniyor...</div>
          </div>
        </main>
      );
    }
    return (
      <main className="main-content tech-service-appointments">
        <div className="content-header">
          <h1>Randevular</h1>
        </div>
        <div className="error-state">
          Teknik servis bilgisi bulunamadı. Lütfen yönetici ile iletişime geçin.
        </div>
      </main>
    );
  }

  return (
    <main className="main-content tech-service-appointments">
      <div className="content-header">
        <div>
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
          <h1>Randevular</h1>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-filter-bar" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-box" style={{ flex: 1, minWidth: '250px' }}>
            {searchLoading ? (
              <span style={{ 
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}>
                <i className="fas fa-spinner fa-spin" style={{ 
                  color: 'var(--text-gray)'
                }}></i>
              </span>
            ) : (
              <i className="fas fa-search"></i>
            )}
            <input
              type="text"
              placeholder="Müşteri adı, telefon ile ara..."
              value={searchInput}
              onChange={handleSearchChange}
              maxLength={100}
              autoComplete="off"
            />
          </div>
          
          <select
            value={filters.durum}
            onChange={(e) => handleFilterChange('durum', e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#ffffff',
              minWidth: '200px'
            }}
          >
            <option value="">Tüm Durumlar</option>
            <option value="Randevu Tarihi Bekleniyor">Randevu Tarihi Bekleniyor</option>
            <option value="İşlemde">İşlemde</option>
            <option value="Randevu Tamamlandı">Randevu Tamamlandı</option>
            <option value="Randevu İptal Edildi">Randevu İptal Edildi</option>
          </select>

          <button
            type="button"
            className={`secondary-btn ${dateFilterLabel ? 'filter-active' : ''}`}
            onClick={() => setIsDateModalOpen(true)}
          >
            <i className="fas fa-filter"></i>
            <span>{dateFilterLabel || 'Tarih Filtrele'}</span>
          </button>
        </div>
      </div>

      <div className="table-container">
        {renderAppointmentsTable()}
      </div>

      {!loading && !error && appointments.length > 0 && (
        <TableListFooter
          totalRecords={totalRecords}
          currentPage={currentPage}
          totalPages={paginationMeta.totalPages || 1}
          onPageChange={setCurrentPage}
          loading={loading}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          onRowsPerPageChange={(value) => handleRowsPerPageChange({ target: { value: String(value) } })}
        />
      )}

      <DateRangeFilterModal
        isOpen={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        initialRange={dateRange}
        onApply={(range) => {
          setDateRange(range);
          setCurrentPage(1);
        }}
      />
    </main>
  );
};

export default TechServiceAppointmentsPage;

